import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Phone, MessageSquare } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { COMPANY, SERVICES } from "@/data/site";

export default function Services() {
  return (
    <>
      <Seo
        title="IT Services | Web Portals, Cloud, Security & Support — LKTH LTD"
        description="Explore LKTH LTD services: custom web portals, website design, web & mobile apps, cloud solutions, IT consulting, cybersecurity, API integrations and managed IT support."
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#0B1F3A] pt-[72px]">
        <div className="absolute inset-0 grid-bg" aria-hidden="true" />
        <div className="absolute inset-0 hero-glow" aria-hidden="true" />
        <div className="container-x relative py-16 sm:py-24">
          <p className="eyebrow eyebrow-light font-mono-alt">Our Services</p>
          <h1 className="mt-4 max-w-2xl font-display text-4xl font-bold text-white sm:text-5xl">
            <MaskedLine>IT services built around</MaskedLine>
            <MaskedLine delay={0.15}>
              <span className="bg-gradient-to-r from-blue-400 to-blue-300 bg-clip-text text-transparent">your business goals</span>
            </MaskedLine>
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
            From a five-page website to a company-wide portal, every project gets the same
            engineering care — clear scope, fixed pricing and support that lasts.
          </p>
        </div>
      </section>

      {/* Service detail blocks */}
      <section className="bg-[#F8FAFC] py-16 sm:py-20">
        <div className="container-x space-y-6">
          {SERVICES.map((s, i) => (
            <article key={s.slug} id={s.slug} className="scroll-mt-28" data-testid={`service-block-${s.slug}`}>
              <Reveal>
                <div className={`card-soft grid gap-8 p-8 sm:p-10 lg:grid-cols-[1fr_1.2fr] lg:gap-14 ${i % 2 ? "lg:[&>*:first-child]:order-2" : ""}`}>
                  <div>
                    <span className="icon-tile"><s.icon className="h-5 w-5" aria-hidden="true" /></span>
                    <h2 className="mt-5 font-display text-2xl font-bold text-[#0B1F3A]">{s.title}</h2>
                    <p className="mt-3 text-sm font-medium text-blue-600 font-mono-alt">
                      {String(i + 1).padStart(2, "0")} / {String(SERVICES.length).padStart(2, "0")}
                    </p>
                  </div>
                  <div>
                    <p className="text-base leading-relaxed text-slate-600">{s.description}</p>
                    <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                      {s.features.map((f) => (
                        <li key={f} className="flex items-start gap-2.5 text-sm text-slate-700">
                          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" aria-hidden="true" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Reveal>
            </article>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0B1F3A] py-16 sm:py-20">
        <div className="container-x flex flex-col items-center justify-between gap-8 text-center lg:flex-row lg:text-left">
          <div>
            <h2 className="font-display text-2xl font-bold text-white sm:text-3xl">Not sure which service you need?</h2>
            <p className="mt-2 max-w-lg text-slate-300">Describe the problem — we will recommend the simplest, most cost-effective fix.</p>
          </div>
          <div className="flex shrink-0 flex-col gap-3 sm:flex-row">
            <Link to="/contact" className="btn-primary" data-testid="services-cta-quote">
              <MessageSquare className="h-4 w-4" /> Book a Free Consultation
            </Link>
            <a href={COMPANY.phoneHref} className="btn-ghost-light" data-testid="services-cta-call">
              <Phone className="h-4 w-4" /> {COMPANY.phoneDisplay}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
